/**
 * Phase 5S.B — Pipeline Drawer.
 *
 * Opens from the Applications table; lets the user advance an application
 * through the state machine with stage-specific data capture. Embeds the
 * timeline + offer-letter PDF download.
 */
import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  ArrowRight, FileText, History, AlertTriangle, CheckCircle2,
  XCircle, Download, Shield,
} from "lucide-react";

const STAGE_LABELS = {
  new: "New", cv_screen: "CV Screen", telephonic: "Telephonic",
  assessment: "Assessment", interview: "Interview", final: "Final Selection",
  offer: "Offer", bgv: "BGV", joining: "Joining", joined: "Joined",
  rejected: "Rejected", declined: "Declined",
};

const STAGE_ORDER = ["new", "cv_screen", "telephonic", "assessment", "interview",
                       "final", "offer", "bgv", "joining", "joined"];

function nextOf(stage) {
  const i = STAGE_ORDER.indexOf(stage);
  return i >= 0 && i + 1 < STAGE_ORDER.length ? STAGE_ORDER[i + 1] : null;
}

export default function PipelineDrawer({ application, onClose, onUpdated }) {
  const [tab, setTab] = useState("advance"); // advance | timeline
  const [timeline, setTimeline] = useState(null);
  const [busy, setBusy] = useState(false);
  const [toStage, setToStage] = useState(nextOf(application?.stage) || "rejected");
  const [override, setOverride] = useState(false);
  const [remarks, setRemarks] = useState("");

  // Stage-specific data — only the stage we're CLOSING needs data
  const [closingData, setClosingData] = useState({});

  const loadTimeline = useCallback(async () => {
    if (!application) return;
    try {
      const { data } = await api.get(`/recruitment/applications/${application.id}/timeline`);
      setTimeline(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  }, [application]);

  useEffect(() => { loadTimeline(); }, [loadTimeline]);

  if (!application) return null;
  const cur = application.stage;
  const isTerminal = ["joined", "rejected", "declined"].includes(cur);

  const downloadOffer = () => {
    window.open(`${API_BASE}/recruitment/applications/${application.id}/offer-letter`, "_blank");
  };

  const submitAdvance = async () => {
    setBusy(true);
    try {
      const payload = { to_stage: toStage, override, remarks };
      // attach closing-stage data under the matching field name
      const fieldMap = {
        cv_screen: "cv_screen", telephonic: "telephonic",
        assessment: "assessment", interview: "interview",
        final: "final_selection", offer: "offer",
        bgv: "bgv", joining: "joining", onboarding: "onboarding",
      };
      // 'final' stage closure is the final_selection data; 'joining' close → onboarding
      const closingField = fieldMap[cur];
      if (closingField && Object.keys(closingData).length > 0) {
        payload[closingField] = closingData;
      }
      // joining → joined transition: onboarding payload goes on 'onboarding'
      if (cur === "joining" && toStage === "joined" && Object.keys(closingData).length > 0) {
        payload.onboarding = closingData;
      }
      await api.post(`/recruitment/applications/${application.id}/advance`, payload);
      toast.success(`Advanced to ${STAGE_LABELS[toStage] || toStage}`);
      setClosingData({});
      setRemarks("");
      onUpdated?.();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl bg-white" data-testid="pipeline-drawer">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {application.name}
            <span className="text-xs font-normal text-slate-500">· {application.mobile}</span>
          </DialogTitle>
          <DialogDescription className="sr-only">
            Pipeline drawer for advancing candidate through hiring stages.
          </DialogDescription>
        </DialogHeader>

        {/* Header strip */}
        <div className="flex items-center gap-2 text-xs bg-slate-50 border rounded p-2">
          <span className="text-slate-500">Current:</span>
          <span className="font-semibold bg-red-100 text-red-900 px-2 py-0.5 rounded">
            {STAGE_LABELS[cur] || cur}
          </span>
          {!isTerminal && (
            <>
              <ArrowRight className="h-3 w-3 text-slate-400" />
              <span className="text-slate-500">Next allowed: {STAGE_LABELS[nextOf(cur)] || "—"}</span>
            </>
          )}
          {application.opening_label && (
            <span className="ml-auto text-slate-500 truncate max-w-[300px]">
              {application.opening_label}
            </span>
          )}
        </div>

        {/* Tab strip */}
        <div className="flex gap-1 text-xs border-b mb-1">
          <button onClick={() => setTab("advance")}
            className={`px-3 py-1.5 ${tab === "advance" ? "border-b-2 border-red-500 font-semibold" : "text-slate-500"}`}
            data-testid="tab-advance">Advance Stage</button>
          <button onClick={() => setTab("timeline")}
            className={`px-3 py-1.5 ${tab === "timeline" ? "border-b-2 border-red-500 font-semibold" : "text-slate-500"}`}
            data-testid="tab-timeline"><History className="h-3 w-3 inline mr-1" />Timeline</button>
        </div>

        {tab === "advance" && (
          <div className="space-y-3 text-sm max-h-[60vh] overflow-y-auto">
            {isTerminal && !override && (
              <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800 flex gap-2 items-start">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                Candidate is in terminal stage <b>{STAGE_LABELS[cur]}</b>. Tick "override" below
                (super_admin only) to reopen and move to a different stage.
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] uppercase text-slate-500">Move to *</Label>
                <Select value={toStage} onValueChange={setToStage}>
                  <SelectTrigger className="h-8 mt-1 text-xs" data-testid="advance-to-stage"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(STAGE_LABELS).map(([k, v]) => (
                      <SelectItem key={k} value={k} disabled={k === cur}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end gap-2">
                <Checkbox checked={override} onCheckedChange={(v) => setOverride(!!v)}
                  id="ov" data-testid="advance-override" />
                <Label htmlFor="ov" className="text-xs text-slate-600 cursor-pointer flex items-center gap-1">
                  <Shield className="h-3 w-3" /> Override (super_admin)
                </Label>
              </div>
            </div>

            <StageDataForm stage={cur} data={closingData} onChange={setClosingData}
              isJoiningToJoined={cur === "joining" && toStage === "joined"} />

            <div>
              <Label className="text-[10px] uppercase text-slate-500">Transition Remarks</Label>
              <Textarea rows={2} value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Optional notes for the audit trail"
                className="mt-1 text-xs" />
            </div>
          </div>
        )}

        {tab === "timeline" && timeline && (
          <div className="space-y-2 text-sm max-h-[60vh] overflow-y-auto">
            {(timeline.stage_history || []).length === 0 && (
              <div className="text-center text-slate-400 text-xs py-6">No transitions yet</div>
            )}
            {(timeline.stage_history || []).slice().reverse().map((h, i) => (
              <Card key={i}><CardContent className="p-3 text-xs">
                <div className="flex items-center gap-2 font-semibold">
                  <span className="bg-slate-100 px-1.5 py-0.5 rounded">{STAGE_LABELS[h.from_stage]}</span>
                  <ArrowRight className="h-3 w-3 text-slate-400" />
                  <span className="bg-red-100 px-1.5 py-0.5 rounded">{STAGE_LABELS[h.to_stage]}</span>
                  {h.override && <span className="bg-amber-100 text-amber-800 px-1 py-0.5 rounded text-[10px]">override</span>}
                  <span className="ml-auto text-[10px] text-slate-400">
                    {new Date(h.at).toLocaleString("en-IN")}
                  </span>
                </div>
                <div className="text-[10px] text-slate-500 mt-1">by {h.by_name} ({h.by_role})</div>
                {h.remarks && <div className="text-xs text-slate-700 mt-1 italic">"{h.remarks}"</div>}
                {h.closing_data && Object.keys(h.closing_data).filter((k) => h.closing_data[k] !== null && h.closing_data[k] !== undefined).length > 0 && (
                  <div className="bg-slate-50 border rounded p-1.5 mt-1.5 text-[10px] grid grid-cols-2 gap-x-3 gap-y-0.5">
                    {Object.entries(h.closing_data).filter(([_k, v]) => v !== null && v !== undefined && v !== "").map(([k, v]) => (
                      <div key={k}>
                        <span className="text-slate-500">{k}:</span>{" "}
                        <span className="text-slate-800 font-medium">{typeof v === "object" ? JSON.stringify(v) : String(v)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent></Card>
            ))}
          </div>
        )}

        <DialogFooter className="flex-wrap gap-2">
          {(application.stage_data?.offer?.final_ctc) && (
            <Button onClick={downloadOffer} variant="outline" className="mr-auto" data-testid="offer-pdf-btn">
              <Download className="h-3.5 w-3.5 mr-1" /> Offer Letter PDF
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>Close</Button>
          {tab === "advance" && (
            <Button onClick={submitAdvance} disabled={busy || (isTerminal && !override)}
              className="brand-bg brand-bg-hover text-white" data-testid="advance-submit">
              {busy ? "Saving…" : `Advance → ${STAGE_LABELS[toStage]}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ----- Stage data form: dynamically renders fields for the CLOSING stage ----- */
function StageDataForm({ stage, data, onChange, isJoiningToJoined }) {
  const set = (k, v) => onChange({ ...data, [k]: v });

  if (stage === "cv_screen") return (
    <Card><CardContent className="p-3 space-y-2">
      <div className="text-[10px] uppercase text-slate-500 mb-1">CV Screen result</div>
      <div className="grid grid-cols-2 gap-2">
        <FieldNum label="Technical match %" value={data.technical_match_pct} onChange={(v) => set("technical_match_pct", v)} />
        <FieldSel label="Salary match" value={data.salary_match} onChange={(v) => set("salary_match", v)}
          opts={[["match","Match"],["stretch","Stretch"],["mismatch","Mismatch"]]} />
        <FieldSel label="Experience match" value={data.experience_match} onChange={(v) => set("experience_match", v)}
          opts={[["match","Match"],["stretch","Stretch"],["mismatch","Mismatch"]]} />
        <FieldBool label="Shortlisted" value={data.shortlisted} onChange={(v) => set("shortlisted", v)} />
      </div>
      <FieldText label="Remarks" value={data.remarks} onChange={(v) => set("remarks", v)} />
    </CardContent></Card>
  );
  if (stage === "telephonic") return (
    <Card><CardContent className="p-3 space-y-2">
      <div className="text-[10px] uppercase text-slate-500 mb-1">Telephonic result</div>
      <div className="grid grid-cols-2 gap-2">
        <FieldBool label="Call Connected" value={data.call_connected} onChange={(v) => set("call_connected", v)} />
        <FieldNum label="Expected Salary (₹)" value={data.expected_salary} onChange={(v) => set("expected_salary", v)} />
        <FieldStr label="Notice Period" value={data.notice_period} onChange={(v) => set("notice_period", v)} />
        <FieldBool label="Open to Relocation" value={data.relocation} onChange={(v) => set("relocation", v)} />
        <FieldBool label="Interested" value={data.interested} onChange={(v) => set("interested", v)} />
      </div>
      <FieldText label="Response" value={data.response} onChange={(v) => set("response", v)} />
    </CardContent></Card>
  );
  if (stage === "assessment") return (
    <Card><CardContent className="p-3 space-y-2">
      <div className="text-[10px] uppercase text-slate-500 mb-1">Assessment result</div>
      <div className="grid grid-cols-2 gap-2">
        <FieldStr label="Test Type" value={data.test_type} onChange={(v) => set("test_type", v)} />
        <FieldNum label="Score" value={data.score} onChange={(v) => set("score", v)} />
        <FieldNum label="Max Score" value={data.max_score} onChange={(v) => set("max_score", v)} />
        <FieldSel label="Pass/Fail" value={data.pass_fail} onChange={(v) => set("pass_fail", v)}
          opts={[["pass","Pass"],["fail","Fail"]]} />
        <FieldStr label="Evaluated By" value={data.evaluated_by} onChange={(v) => set("evaluated_by", v)} />
      </div>
      <FieldText label="Remarks" value={data.remarks} onChange={(v) => set("remarks", v)} />
    </CardContent></Card>
  );
  if (stage === "interview") return (
    <Card><CardContent className="p-3 space-y-2">
      <div className="text-[10px] uppercase text-slate-500 mb-1">Interview result</div>
      <div className="grid grid-cols-2 gap-2">
        <FieldNum label="Round" value={data.round_number} onChange={(v) => set("round_number", v)} />
        <FieldNum label="Rating /10" value={data.rating} onChange={(v) => set("rating", v)} />
      </div>
      <FieldText label="Strengths" value={data.strengths} onChange={(v) => set("strengths", v)} />
      <FieldText label="Concerns" value={data.concerns} onChange={(v) => set("concerns", v)} />
      <FieldBool label="Selected for Next Round" value={data.selected_for_next_round} onChange={(v) => set("selected_for_next_round", v)} />
    </CardContent></Card>
  );
  if (stage === "final") return (
    <Card><CardContent className="p-3 space-y-2">
      <div className="text-[10px] uppercase text-slate-500 mb-1">Final Selection</div>
      <div className="grid grid-cols-2 gap-2">
        <FieldNum label="Offered CTC (₹)" value={data.offered_ctc} onChange={(v) => set("offered_ctc", v)} />
        <FieldDate label="Joining Date" value={data.joining_date} onChange={(v) => set("joining_date", v)} />
        <FieldSel label="Status" value={data.selection_status} onChange={(v) => set("selection_status", v)}
          opts={[["selected","Selected"],["hold","Hold"],["rejected","Rejected"],["backup","Backup"]]} />
      </div>
      <FieldText label="Remarks" value={data.remarks} onChange={(v) => set("remarks", v)} />
    </CardContent></Card>
  );
  if (stage === "offer") return (
    <Card><CardContent className="p-3 space-y-2">
      <div className="text-[10px] uppercase text-slate-500 mb-1">Offer details · used by Offer Letter PDF</div>
      <div className="grid grid-cols-2 gap-2">
        <FieldNum label="Final CTC (₹) *" value={data.final_ctc} onChange={(v) => set("final_ctc", v)} />
        <FieldNum label="Joining Bonus (₹)" value={data.joining_bonus} onChange={(v) => set("joining_bonus", v)} />
        <FieldNum label="Relocation Allowance (₹)" value={data.relocation_amount} onChange={(v) => set("relocation_amount", v)} />
        <FieldNum label="Notice Buyout (₹)" value={data.notice_buyout} onChange={(v) => set("notice_buyout", v)} />
        <FieldDate label="Offer Validity Date" value={data.offer_validity_date} onChange={(v) => set("offer_validity_date", v)} />
        <FieldNum label="Version" value={data.offer_version || 1} onChange={(v) => set("offer_version", v)} />
        <FieldBool label="Joining Confirmed" value={data.joining_confirmed} onChange={(v) => set("joining_confirmed", v)} />
      </div>
    </CardContent></Card>
  );
  if (stage === "bgv") return (
    <Card><CardContent className="p-3 space-y-2">
      <div className="text-[10px] uppercase text-slate-500 mb-1">Background Verification</div>
      <FieldStr label="Agency" value={data.agency} onChange={(v) => set("agency", v)} />
      <FieldSel label="Overall Result" value={data.overall} onChange={(v) => set("overall", v)}
        opts={[["pending","Pending"],["clear","Clear"],["discrepancy","Discrepancy"]]} />
      <FieldDate label="Completed Date" value={data.completed_date} onChange={(v) => set("completed_date", v)} />
      <FieldText label="Remarks" value={data.remarks} onChange={(v) => set("remarks", v)} />
    </CardContent></Card>
  );
  if (stage === "joining") return (
    <Card><CardContent className="p-3 space-y-2">
      <div className="text-[10px] uppercase text-slate-500 mb-1">{isJoiningToJoined ? "Onboarding (also captured when finalising)" : "Joining"}</div>
      {!isJoiningToJoined && (
        <>
          <div className="grid grid-cols-2 gap-2">
            <FieldDate label="Joining Date" value={data.joining_date} onChange={(v) => set("joining_date", v)} />
            <FieldBool label="Documents Received" value={data.documents_received} onChange={(v) => set("documents_received", v)} />
            <FieldBool label="Medical Done" value={data.medical_done} onChange={(v) => set("medical_done", v)} />
            <FieldSel label="Status" value={data.status} onChange={(v) => set("status", v)}
              opts={[["joined","Joined"],["no_show","No Show"],["deferred","Deferred"]]} />
          </div>
        </>
      )}
      {isJoiningToJoined && (
        <>
          <div className="grid grid-cols-2 gap-2">
            <FieldBool label="Induction Done" value={data.induction_done} onChange={(v) => set("induction_done", v)} />
            <FieldBool label="Asset Allocation" value={data.asset_allocation_done} onChange={(v) => set("asset_allocation_done", v)} />
            <FieldBool label="Email Created" value={data.email_created} onChange={(v) => set("email_created", v)} />
            <FieldBool label="UAN / PF Enrolled" value={data.uan_pf_enrolled} onChange={(v) => set("uan_pf_enrolled", v)} />
            <FieldBool label="ESIC Enrolled" value={data.esic_enrolled} onChange={(v) => set("esic_enrolled", v)} />
            <FieldBool label="ID Card Issued" value={data.id_card_issued} onChange={(v) => set("id_card_issued", v)} />
          </div>
          <FieldText label="Buddy / Mentor Employee ID" value={data.buddy_employee_id} onChange={(v) => set("buddy_employee_id", v)} />
          <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-xs space-y-1.5 mt-2">
            <div className="flex items-center gap-2">
              <Checkbox checked={!!data.create_employee_record}
                onCheckedChange={(v) => set("create_employee_record", !!v)}
                id="cer" data-testid="onboarding-create-emp" />
              <Label htmlFor="cer" className="text-xs font-semibold flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3 text-emerald-700" />
                Auto-create employee record in HR Master
              </Label>
            </div>
            {data.create_employee_record && (
              <EmployeePayloadForm data={data.employee_payload || {}}
                onChange={(p) => set("employee_payload", p)} />
            )}
          </div>
        </>
      )}
    </CardContent></Card>
  );
  return (
    <Card><CardContent className="p-3 text-xs text-slate-500">
      No additional data needed when transitioning from <b>{STAGE_LABELS[stage] || stage}</b>.
    </CardContent></Card>
  );
}

function EmployeePayloadForm({ data, onChange }) {
  const [units, setUnits] = useState([]);
  const [depts, setDepts] = useState([]);
  useEffect(() => {
    api.get("/units").then((r) => setUnits(r.data || []));
    api.get("/departments").then((r) => setDepts(r.data || []));
  }, []);
  const set = (k, v) => onChange({ ...data, [k]: v });
  return (
    <div className="grid grid-cols-2 gap-1.5 mt-1.5">
      <FieldStr label="Punch ID *" value={data.punch_id} onChange={(v) => set("punch_id", v)} />
      <FieldStr label="Name *" value={data.name} onChange={(v) => set("name", v)} />
      <FieldStr label="Father / Husband Name" value={data.father_husband_name} onChange={(v) => set("father_husband_name", v)} />
      <FieldStr label="Designation" value={data.designation} onChange={(v) => set("designation", v)} />
      <FieldSelObj label="Department *" value={data.department_id} onChange={(v) => set("department_id", v)}
        opts={depts.map((d) => [d.id, d.name])} />
      <FieldSelObj label="Unit *" value={data.unit_id} onChange={(v) => set("unit_id", v)}
        opts={units.map((u) => [u.id, u.name])} />
      <FieldDate label="DOJ *" value={data.doj} onChange={(v) => set("doj", v)} />
      <FieldNum label="Annual CTC (₹)" value={data.ctc} onChange={(v) => set("ctc", v)} />
    </div>
  );
}

/* ---- Small field helpers ---- */
const lbl = "text-[10px] uppercase text-slate-500";
function FieldStr({ label, value, onChange }) {
  return <div><Label className={lbl}>{label}</Label>
    <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className="h-7 text-xs mt-0.5" /></div>;
}
function FieldNum({ label, value, onChange }) {
  return <div><Label className={lbl}>{label}</Label>
    <Input type="number" value={value ?? ""}
      onChange={(e) => onChange(e.target.value === "" ? null : parseFloat(e.target.value))}
      className="h-7 text-xs mt-0.5" /></div>;
}
function FieldDate({ label, value, onChange }) {
  return <div><Label className={lbl}>{label}</Label>
    <Input type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} className="h-7 text-xs mt-0.5" /></div>;
}
function FieldBool({ label, value, onChange }) {
  return (
    <div className="flex items-center gap-1.5 pt-3">
      <Checkbox checked={!!value} onCheckedChange={(v) => onChange(!!v)} />
      <Label className="text-xs">{label}</Label>
    </div>
  );
}
function FieldSel({ label, value, onChange, opts }) {
  return <div><Label className={lbl}>{label}</Label>
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger className="h-7 text-xs mt-0.5"><SelectValue placeholder="—" /></SelectTrigger>
      <SelectContent>{opts.map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
    </Select></div>;
}
function FieldSelObj({ label, value, onChange, opts }) {
  return <div><Label className={lbl}>{label}</Label>
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger className="h-7 text-xs mt-0.5"><SelectValue placeholder="—" /></SelectTrigger>
      <SelectContent>{opts.map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
    </Select></div>;
}
function FieldText({ label, value, onChange }) {
  return <div><Label className={lbl}>{label}</Label>
    <Textarea rows={2} value={value || ""} onChange={(e) => onChange(e.target.value)} className="text-xs mt-0.5" /></div>;
}
